import { useState } from 'react';
import {
  bulkUpsertMaterials,
  ExcelConfig,
  exportVatTuSanPhamExcelFromApi,
  extractBulkUpsertErrors,
  mapBulkUpsertErrorsToExcel
} from './utils';
import { BulkDeleteDialog, ErpDataTableV2, ExcelImportDialog, ExcelImportErrorDialog } from '@/components/custom/erp';
import { useVatTuSanPhamContext, VatTuSanPhamProvider } from './context';
import { ActionBar, DeleteDialog, FormDialog } from './components';
import type { ColumnMapping, ValidationError } from '@/lib/excel';
import { useExcel, useExcelProcessor } from '@/hooks';
import { getItemColumns } from './cols-definition';
import { useAuth } from '@/contexts/auth-context';
import { toast } from '@/hooks/use-toast';
import type { ImportBody } from './utils';

function VatTuSanPhamPageContent() {
  const [showErrorDialog, setShowErrorDialog] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [errorData, setErrorData] = useState<{
    errors: ValidationError[];
    columnMapping: Record<string, ColumnMapping>;
  } | null>(null);
  const { entity, entityUnit } = useAuth();
  const { processFile } = useExcelProcessor(ExcelConfig);
  const { getUUIDByCode } = useExcel();

  const {
    handleSearchChange,
    searchState,
    hasFilterParams,
    showForm,
    selectedRowIndex,
    handleRowClick,
    handleRowDoubleClick,
    handleTabChange,

    // CRUD from context
    isLoading,
    currentPage,
    totalItems,
    handlePageChange,
    tables,
    refreshData,
    deleteItem,
    data,
    donViTinhs,
    showBulkDelete,
    handleCloseBulkDelete,
    isImportDialogOpen,
    setIsImportDialogOpen,
    handleOpenImportExcel
  } = useVatTuSanPhamContext();

  const handleImportBodies = async (
    bodies: ImportBody[],
    columnMapping: Record<string, ColumnMapping>
  ): Promise<boolean> => {
    if (!entity?.slug) {
      toast({
        description: 'Không tìm thấy thông tin đơn vị',
        variant: 'destructive'
      });
      return false;
    }

    try {
      const response = await bulkUpsertMaterials(entity.slug, bodies);
      await refreshData();
      toast({
        description: `Đã nhập ${response.total} vật tư, sản phẩm (${response.created} mới, ${response.updated} cập nhật)`,
        variant: 'default'
      });
      return true;
    } catch (error) {
      console.error('Error importing materials from Excel:', error);
      const backendErrors = extractBulkUpsertErrors(error);
      if (backendErrors.length > 0) {
        setErrorData({
          errors: mapBulkUpsertErrorsToExcel(backendErrors, columnMapping),
          columnMapping
        });
        setShowErrorDialog(true);
      }
      return false;
    }
  };

  const handleImportFromExcel = async (data: { errors?: string; file: File | null; sheetIndex?: string }) => {
    if (!entityUnit?.uuid) {
      toast({
        description: 'Không tìm thấy thông tin đơn vị',
        variant: 'destructive'
      });
      return;
    }

    if (!data.file) {
      toast({
        description: 'Vui lòng chọn file Excel',
        variant: 'destructive'
      });
      return;
    }

    const maxErrors = parseInt(data.errors || '200', 10);
    const sheetIndex = parseInt(data.sheetIndex || '0', 10);

    const result = await processFile({
      file: data.file,
      sheetIndex,
      maxErrors,
      getUUIDByCode,
      context: { entityUnitUUID: entityUnit.uuid }
    });

    if (result.success && result.data) {
      await handleImportBodies(result.data, result.columnMapping || {});
      setIsImportDialogOpen(false);
    } else if (result.errors && result.columnMapping) {
      setErrorData({ errors: result.errors, columnMapping: result.columnMapping });
      setShowErrorDialog(true);
      setIsImportDialogOpen(false);
    }
  };

  const handleErrorDialogClose = () => {
    setShowErrorDialog(false);
    setErrorData(null);
  };

  const handleExportExcel = async () => {
    if (!entity?.slug) {
      toast({
        description: 'Không tìm thấy thông tin đơn vị',
        variant: 'destructive'
      });
      return;
    }

    const filters = hasFilterParams ? searchState.filterParams : undefined;

    setIsExporting(true);
    try {
      const exportedCount = await exportVatTuSanPhamExcelFromApi({
        entitySlug: entity.slug,
        filters
      });

      if (!exportedCount) {
        toast({
          description: 'Không có dữ liệu để kết xuất',
          variant: 'default'
        });
      }
    } catch (error) {
      console.error('Error exporting materials to Excel:', error);
      toast({
        description: 'Có lỗi xảy ra khi kết xuất Excel',
        variant: 'destructive'
      });
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <div className='flex h-screen w-full flex-col overflow-auto'>
      {showForm && <FormDialog />}
      <div className={showForm ? 'hidden' : 'flex min-w-0 flex-1 flex-col overflow-hidden'}>
        <ActionBar onImportExcel={handleOpenImportExcel} onExportExcel={handleExportExcel} />

        <div data-excel-operation-overlay-root className='relative flex min-w-0 flex-1 flex-col overflow-hidden'>
          <ErpDataTableV2
            tables={tables}
            onRowClick={handleRowClick}
            onRowDoubleClick={handleRowDoubleClick}
            selectedRowId={selectedRowIndex || undefined}
            totalItems={totalItems}
            currentPage={currentPage}
            onSearchChange={handleSearchChange}
            onPageChange={handlePageChange}
            pageSize={100}
            onTabChange={(_event: unknown, value: number) => {
              const tabPaths = ['vat-tu-san-pham', 'lo-hang', 'kho-hang', 'vi-tri-kho-hang'];
              handleTabChange(tabPaths[value]);
            }}
            loading={isLoading || searchState.isSearching}
          />
        </div>
      </div>

      <DeleteDialog />

      {showBulkDelete && (
        <BulkDeleteDialog
          title='Vật tư, sản phẩm'
          onClose={handleCloseBulkDelete}
          columns={getItemColumns(donViTinhs).filter(col => col.field !== 'checkbox')}
          dataRows={data}
          handleDelete={async (uuid: string) => {
            await deleteItem(uuid);
          }}
          onComplete={refreshData}
        />
      )}

      {isImportDialogOpen && (
        <ExcelImportDialog
          open={isImportDialogOpen}
          onClose={() => setIsImportDialogOpen(false)}
          onSubmit={handleImportFromExcel}
          title='Nhập dữ liệu từ Excel'
        />
      )}

      {showErrorDialog && errorData && (
        <ExcelImportErrorDialog
          open={showErrorDialog}
          onClose={handleErrorDialogClose}
          errors={errorData.errors}
          columnMapping={errorData.columnMapping}
        />
      )}
    </div>
  );
}

export default function VatTuSanPhamPage() {
  return (
    <VatTuSanPhamProvider>
      <VatTuSanPhamPageContent />
    </VatTuSanPhamProvider>
  );
}
